import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Save, Loader2, Lock, ShoppingBag, AlertTriangle, RotateCcw, Info, Hash,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SOURCE_LABEL = {
  CORE: "Always on",
  PLAN: "In your plan",
  ADDON: "Add-on you bought",
  OVERRIDE: "Enabled for you",
  NOT_INCLUDED: "Not in your plan",
};

/* ------------------------------------------------------------------ features */
/**
 * Which features this organisation actually wants.
 *
 * Deliberately separate from entitlement: the plan and the add-ons decide what
 * the owner *may* use, and this decides what they want on their screens. You
 * cannot switch on something you have not been given, and switching something
 * off hides it rather than deleting anything — which is why the record count
 * is printed next to every switch before you touch it.
 */
export function FeatureSwitches({ readOnly, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    api.get("/organization/modules").then(({ data }) => setRows(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const out = {};
    rows.forEach((r) => { (out[r.category] ||= []).push(r); });
    return out;
  }, [rows]);

  const apply = async (next) => {
    setBusy(true);
    try {
      await api.put("/organization/modules", { disabled: next });
      toast.success("Features updated");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
    setBusy(false);
    setPending(null);
  };

  const toggle = (row, on) => {
    const disabled = rows.filter((r) => !r.enabled && r.entitled).map((r) => r.key);
    const next = on ? disabled.filter((k) => k !== row.key) : [...disabled, row.key];
    // Switching something off that has records behind it is worth a beat.
    if (!on && row.usage?.count) { setPending({ row, next }); return; }
    apply(next);
  };

  return (
    <div className="space-y-5" data-testid="feature-switches">
      <p className="max-w-2xl text-sm text-slate-500">
        Turn off anything you do not use and it disappears from the menu and from
        everyone's screens. Nothing is deleted — switch it back on and your records
        are exactly where you left them.
      </p>

      {Object.entries(groups).map(([group, items]) => (
        <div key={group} className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {group}
          </div>
          <div className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
            {items.map((r) => (
              <div key={r.key} className="flex items-start justify-between gap-4 px-4 py-3"
                data-testid={`feature-${r.key}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{r.name}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      r.source === "CORE" ? "bg-slate-100 text-slate-500"
                      : r.source === "ADDON" ? "bg-violet-50 text-violet-700"
                      : r.source === "NOT_INCLUDED" ? "bg-amber-50 text-amber-700"
                      : "bg-emerald-50 text-emerald-700"}`}>
                      {SOURCE_LABEL[r.source]}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
                    {r.description}
                  </p>
                  {r.usage?.count > 0 && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      {r.usage.count.toLocaleString("en-IN")} {r.usage.noun} on record
                    </p>
                  )}
                  {!r.entitled && r.addon_price_monthly ? (
                    <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
                      <ShoppingBag size={11} /> ₹{r.addon_price_monthly}/month add-on —
                      buy it under Billing
                    </p>
                  ) : null}
                </div>
                <div className="shrink-0 pt-0.5">
                  {r.is_core ? (
                    <span title="This one cannot be switched off"
                      className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                      <Lock size={12} /> Always on
                    </span>
                  ) : (
                    <Switch checked={r.enabled} disabled={readOnly || busy || !r.entitled}
                      data-testid={`feature-toggle-${r.key}`}
                      onCheckedChange={(v) => toggle(r, v)} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {pending && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          data-testid="feature-confirm">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-start gap-2">
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />
              <div>
                <div className="text-sm font-semibold">
                  Switch off {pending.row.name}?
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  You have{" "}
                  <strong>{pending.row.usage.count.toLocaleString("en-IN")}{" "}
                  {pending.row.usage.noun}</strong> behind this feature. They stay exactly
                  as they are — the screens are just hidden until you switch it back on.
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setPending(null)}>
                Keep it on
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                data-testid="feature-confirm-off" onClick={() => apply(pending.next)}>
                Switch it off
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ numbering */
export function DocumentNumbering({ readOnly }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/organization/numbering")
      .then(({ data }) => { setData(data); setEdits({}); })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (key, field, value) =>
    setEdits((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: value } }));

  const rowOf = (s) => ({ ...s, ...(edits[s.document_type] || {}) });

  const preview = (s) => {
    const r = rowOf(s);
    const pad = Math.max(Number(r.padding) || 4, 1);
    const num = String(Math.max(Number(r.next_number) || 1, 1)).padStart(pad, "0");
    const fy = data.financial_year;
    return String(r.format || "")
      .replace("{FY_LONG}", fy)
      .replace("{FY}", `${fy.slice(2, 4)}-${fy.slice(-2)}`)
      .replace("{YY}", fy.slice(2, 4))
      .replace("{MM}", "08")
      .replace("{####}", num);
  };

  const save = async () => {
    const series = Object.entries(edits).map(([document_type, v]) => ({
      document_type,
      format: v.format,
      padding: v.padding !== undefined ? Number(v.padding) : undefined,
      next_number: v.next_number !== undefined ? Number(v.next_number) : undefined,
    }));
    if (!series.length) { toast.error("Nothing changed"); return; }
    setBusy(true);
    try {
      const { data: fresh } = await api.put("/organization/numbering", { series });
      setData(fresh); setEdits({});
      toast.success("Numbering updated");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const dirty = Object.keys(edits).length;

  return (
    <div className="space-y-4" data-testid="document-numbering">
      <p className="max-w-2xl text-sm text-slate-500">
        Your own prefix and your own starting number, per document type and per
        financial year. Moving in from another system? Set the next number to carry on
        where you left off.
      </p>

      <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
        {data.placeholders.map((p) => (
          <span key={p.token} className="rounded border border-slate-200 bg-slate-50 px-2 py-1">
            <code className="font-mono text-slate-700">{p.token}</code> — {p.means}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="numbering-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Document</th>
              <th className="px-3 py-2.5 text-left font-semibold">Format</th>
              <th className="px-3 py-2.5 text-right font-semibold">Next no.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Digits</th>
              <th className="px-4 py-2.5 text-left font-semibold">Next will be</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.series.map((s) => {
              const r = rowOf(s);
              return (
                <tr key={s.document_type} data-testid={`numbering-${s.document_type}`}>
                  <td className="px-4 py-2">
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-[10px] text-slate-400">
                      {s.issued} issued in {s.financial_year}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Input className="h-8 w-44 font-mono text-xs" disabled={readOnly}
                      data-testid={`numbering-format-${s.document_type}`}
                      value={r.format}
                      onChange={(e) => patch(s.document_type, "format", e.target.value)} />
                  </td>
                  <td className="px-3 py-2">
                    <Input className="ml-auto h-8 w-24 text-right font-mono text-xs"
                      disabled={readOnly}
                      data-testid={`numbering-next-${s.document_type}`}
                      value={r.next_number}
                      onChange={(e) => patch(s.document_type, "next_number", e.target.value)} />
                  </td>
                  <td className="px-3 py-2">
                    <Input className="ml-auto h-8 w-16 text-right font-mono text-xs"
                      disabled={readOnly}
                      data-testid={`numbering-pad-${s.document_type}`}
                      value={r.padding}
                      onChange={(e) => patch(s.document_type, "padding", e.target.value)} />
                  </td>
                  <td className="px-4 py-2">
                    <span className="font-mono text-xs font-semibold">{preview(s)}</span>
                    {s.issued > 0 && (
                      <div className="text-[10px] text-slate-400">
                        cannot go below {s.issued + 1}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-start gap-2 text-[11px] text-slate-600">
          <Info size={13} className="mt-px shrink-0 text-slate-400" />
          A number that has already gone out cannot be reused, so the counter only
          moves forwards. Rule 46 caps the whole number at 16 characters.
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            {!!dirty && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={load}
                data-testid="numbering-reset">
                <RotateCcw size={14} /> Discard
              </Button>
            )}
            <Button size="sm" disabled={!dirty || busy} onClick={save}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="numbering-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Hash size={14} />}
              Save numbering
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ preferences */
export function Preferences({ readOnly }) {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/organization/preferences")
      .then(({ data }) => { setState(data); setDraft(data.values); })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => state && Object.keys(draft).some((k) => String(draft[k]) !== String(state.values[k])),
    [draft, state]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/organization/preferences", { preferences: draft });
      setState((s) => ({ ...s, values: data.values }));
      setDraft(data.values);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!state) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="org-preferences">
      <p className="max-w-2xl text-sm text-slate-500">
        How the system behaves day to day. Each of these changes what actually
        happens, not just what is shown.
      </p>

      <div className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
        {state.options.map((o) => (
          <div key={o.key} className="flex items-start justify-between gap-4 px-4 py-3"
            data-testid={`pref-${o.key}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{o.label}</div>
              <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{o.help}</p>
              {!o.available && (
                <p className="mt-1 text-[11px] text-amber-700">
                  Applies once the related feature is switched on.
                </p>
              )}
            </div>
            <div className="shrink-0 pt-0.5">
              {o.type === "bool" ? (
                <Switch checked={!!draft[o.key]} disabled={readOnly || !o.available}
                  data-testid={`pref-toggle-${o.key}`}
                  onCheckedChange={(v) => setDraft((d) => ({ ...d, [o.key]: v }))} />
              ) : o.type === "choice" ? (
                <Select value={String(draft[o.key] ?? "")} disabled={readOnly || !o.available}
                  onValueChange={(v) => setDraft((d) => ({ ...d, [o.key]: v }))}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid={`pref-select-${o.key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {o.choices.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input className="h-8 w-20 text-right font-mono text-xs"
                  disabled={readOnly || !o.available}
                  data-testid={`pref-input-${o.key}`}
                  value={draft[o.key]}
                  onChange={(e) => setDraft((d) => ({ ...d, [o.key]: e.target.value }))} />
              )}
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-2">
          {dirty && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={load}>
              <RotateCcw size={14} /> Discard
            </Button>
          )}
          <Button size="sm" disabled={!dirty || busy} onClick={save}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="preferences-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save preferences
          </Button>
        </div>
      )}
    </div>
  );
}
